import type React from 'react'
import type { Dispatch } from '@reduxjs/toolkit'
import type { Business, User } from '@pbt/pbt-ui-components'

import { triggerErrorDoggo } from '../../../add-payment-dialog-v2/utils/errorDoggoUtils'
import { RefundFlowState } from '../constants/refundDialogV2Constants'
import { getRefundInitiationFailureMessage } from './refundErrorMapping'

interface InitiateRefundTransactionDependencies {
  currentBusiness?: Business | null
  currentUser?: User | null
  dispatch: React.Dispatch<any>
  initiateRefundTransactionMutation: (options: {
    variables: {
      input: {
        amount: number
        externalDeviceId: string | null
        originalPaymentId: string
        refundInvoiceId: string
      }
    }
  }) => Promise<any>
  reduxDispatch: Dispatch<any>
  refundAmount: number
  refundInvoiceId: string
  selectedPaymentId: string
  selectedPaymentTerminal: string | null
  setIsLoading: (loading: boolean) => void
}

/**
 * Handles the refund transaction mutation with error handling.
 * Extracted to reduce cognitive complexity in useMemo.
 */
export const initiateRefundTransaction = async (
  deps: InitiateRefundTransactionDependencies,
): Promise<void> => {
  const {
    dispatch,
    reduxDispatch,
    setIsLoading,
    initiateRefundTransactionMutation,
    refundAmount,
    selectedPaymentTerminal,
    selectedPaymentId,
    refundInvoiceId,
    currentUser,
    currentBusiness,
  } = deps

  try {
    const result = await initiateRefundTransactionMutation({
      variables: {
        input: {
          amount: refundAmount,
          originalPaymentId: selectedPaymentId,
          refundInvoiceId,
          externalDeviceId: selectedPaymentTerminal,
        },
      },
    })

    if (!result?.data?.initiateRefundTransaction || result.errors) {
      dispatch({ type: 'CANCEL_TRANSACTION' })
      const errorMessage = getRefundInitiationFailureMessage()
      const backendErrorMessage = result.errors?.[0]?.message || errorMessage
      triggerErrorDoggo(
        reduxDispatch,
        backendErrorMessage,
        500,
        currentUser,
        currentBusiness,
      )
      dispatch({
        type: 'SET_REFUND_ERROR',
        payload: {
          message: errorMessage,
          severity: 'error',
          status: 500,
        },
      })
    } else {
      dispatch({
        type: 'SET_STATE',
        payload: RefundFlowState.REFUND_PROCESSING,
      })
      dispatch({
        type: 'SET_TRANSACTION_ID',
        payload: result.data.initiateRefundTransaction.id,
      })
    }
  } catch (err) {
    dispatch({ type: 'CANCEL_TRANSACTION' })
    const errorMessage = getRefundInitiationFailureMessage()
    const backendErrorMessage = (err as any)?.message || errorMessage
    triggerErrorDoggo(
      reduxDispatch,
      backendErrorMessage,
      500,
      currentUser,
      currentBusiness,
    )
    dispatch({
      type: 'SET_REFUND_ERROR',
      payload: {
        message: errorMessage,
        severity: 'error',
        status: 500,
      },
    })
  } finally {
    setIsLoading(false)
  }
}
